/**
 * Mapeo entre species_details.extended_info (JSONB) y el formulario admin.
 * Alineado con SpeciesDetail en src/utils/speciesDatabase.ts y speciesDetailsAdapter.
 */

export interface ExtendedInfoStringGroup {
    key: string;
    label: string;
    fields: { key: string; label: string; rows?: number }[];
}

export const EXTENDED_INFO_GROUPS: ExtendedInfoStringGroup[] = [
    {
        key: 'biology',
        label: 'Biología y hábitat',
        fields: [
            { key: 'maxSizeWorld', label: 'Tamaño máximo mundial' },
            { key: 'typicalSizeSpain', label: 'Talla habitual en España' },
            { key: 'lifespan', label: 'Esperanza de vida' },
            { key: 'habitat', label: 'Hábitat' },
            { key: 'maxSpeed', label: 'Velocidad máxima' },
        ],
    },
    {
        key: 'distribution',
        label: 'Distribución',
        fields: [
            { key: 'global', label: 'Global' },
            { key: 'mediterranean', label: 'Mediterráneo' },
            { key: 'atlanticAndalusia', label: 'Atlántico / Andalucía' },
            { key: 'canaries', label: 'Canarias' },
            { key: 'cantabric', label: 'Cantábrico' },
        ],
    },
    {
        key: 'behavior',
        label: 'Comportamiento',
        fields: [
            { key: 'diet', label: 'Dieta' },
            { key: 'migration', label: 'Migración' },
            { key: 'habits', label: 'Hábitos' },
        ],
    },
    {
        key: 'reproduction',
        label: 'Reproducción',
        fields: [
            { key: 'sexualMaturity', label: 'Madurez sexual' },
            { key: 'spawningTime', label: 'Época de desove' },
            { key: 'strategy', label: 'Estrategia reproductiva' },
        ],
    },
    {
        key: 'fishing',
        label: 'Pesca en España',
        fields: [
            { key: 'bestSeasons', label: 'Mejores temporadas' },
            { key: 'whereToFish', label: 'Dónde pescar' },
            { key: 'recommendedTechniques', label: 'Técnicas recomendadas' },
            { key: 'fightingBehavior', label: 'Comportamiento al combate' },
        ],
    },
    {
        key: 'records',
        label: 'Récords y curiosidades',
        fields: [
            { key: 'worldRecord', label: 'Récord mundial' },
            { key: 'spanishRecords', label: 'Récords en España' },
            { key: 'curiosities', label: 'Curiosidades', rows: 4 },
            { key: 'gastronomicValue', label: 'Valor gastronómico' },
        ],
    },
];

export type ExtendedInfoFormState = {
    alternativeNames: string;
    groups: Record<string, Record<string, string>>;
};

export function emptyExtendedInfoFormState(): ExtendedInfoFormState {
    const groups: Record<string, Record<string, string>> = {};
    for (const group of EXTENDED_INFO_GROUPS) {
        groups[group.key] = {};
        for (const field of group.fields) {
            groups[group.key][field.key] = '';
        }
    }
    return { alternativeNames: '', groups };
}

function readStringGroup(
    source: Record<string, unknown> | undefined,
    group: ExtendedInfoStringGroup
): Record<string, string> {
    const raw = (source?.[group.key] ?? {}) as Record<string, unknown>;
    const out: Record<string, string> = {};
    for (const field of group.fields) {
        const value = raw[field.key];
        out[field.key] = typeof value === 'string' ? value : '';
    }
    return out;
}

function writeStringGroup(
    target: Record<string, unknown>,
    groupKey: string,
    values: Record<string, string>
): void {
    const cleaned: Record<string, string> = {};
    for (const [key, value] of Object.entries(values)) {
        const trimmed = value.trim();
        if (trimmed) cleaned[key] = trimmed;
    }
    if (Object.keys(cleaned).length > 0) {
        target[groupKey] = cleaned;
    }
}

export function extendedInfoToFormState(extendedInfo: Record<string, unknown> | null | undefined): ExtendedInfoFormState {
    const base = emptyExtendedInfoFormState();
    const ext = extendedInfo ?? {};
    const alt = ext.alternativeNames;
    if (Array.isArray(alt)) {
        base.alternativeNames = alt.filter(v => typeof v === 'string' && v.trim()).join(', ');
    }
    for (const group of EXTENDED_INFO_GROUPS) {
        base.groups[group.key] = readStringGroup(ext, group);
    }
    return base;
}

export function formStateToExtendedInfo(form: ExtendedInfoFormState): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    const alt = form.alternativeNames
        .split(',')
        .map(v => v.trim())
        .filter(Boolean);
    if (alt.length > 0) out.alternativeNames = alt;
    for (const group of EXTENDED_INFO_GROUPS) {
        writeStringGroup(out, group.key, form.groups[group.key] ?? {});
    }
    return out;
}

export function hasExtendedInfoContent(extendedInfo: Record<string, unknown>): boolean {
    return Object.keys(extendedInfo).length > 0;
}
